<template>
  <div class="flex flex-row justify-center align-middle">
    <Card class="p-4 sm:basis-full md:basis-2/3 xl:basis-1/2 2xl:basis-1/3" pt:title:class="font-bold text-2xl text-center">
      <template #title>Change Password</template>
      <template #subtitle><p class="text-center text-sm font-normal">Enter your new password and confirm it using the fields below.</p> </template>
      <template #content>
        <Message v-if="message" :closeable="false" severity="error">{{ message }}</Message>
        <div class="flex flex-wrap gap-2">
          <Password placeholder="New Password" v-model="password" class="w-full" inputClass="w-full" :feedback="false" toggleMask />
          <Password placeholder="Confirm Password" v-model="verifyPassword" class="w-full" inputClass="w-full" :feedback="false" toggleMask />
          <div v-if="requirements">
            <h2 class="font-semibold">Password Requirements:</h2>
            <ul class="list-inside mt-2">
              <li class="text-sm">
                <i
                  v-if="password.length >= requirements.minLength && password.length <= requirements.maxLength"
                  class="pi pi-check me-2 text-success-500"
                ></i>
                <i v-else class="pi pi-times me-2 text-danger-500"></i>
                Length between {{ requirements.minLength }} and {{ requirements.maxLength }} characters
              </li>
              <li v-if="requirements.maxRepeats > 0" class="text-sm">
                <i v-if="password.length > 0 && getMaxRepeats() <= requirements.maxRepeats" class="pi pi-check me-2 text-success-500"></i>
                <i v-else class="pi pi-times me-2 text-danger-500"></i>
                No more than {{ requirements.maxRepeats }} repeated consecutive characters
              </li>
              <li v-if="requirements.requireLower" class="text-sm">
                <i v-if="password.toUpperCase() != password" class="pi pi-check me-2 text-success-500"></i>
                <i v-else class="pi pi-times me-2 text-danger-500"></i>
                At least one lowercase letter
              </li>
              <li v-if="requirements.requireUpper" class="text-sm">
                <i v-if="password.toLowerCase() != password" class="pi pi-check me-2 text-success-500"></i>
                <i v-else class="pi pi-times me-2 text-danger-500"></i>
                At least one uppercase letter
              </li>
              <li v-if="requirements.requireNumber" class="text-sm">
                <i v-if="/\d/.test(password)" class="pi pi-check me-2 text-success-500"></i>
                <i v-else class="pi pi-times me-2 text-danger-500"></i>
                At least one number
              </li>
              <li v-if="requirements.requireSpecial" class="text-sm">
                <i v-if="hasSpecialCharacter()" class="pi pi-check me-2 text-success-500"></i>
                <i v-else class="pi pi-times me-2 text-danger-500"></i>
                At least one special character
              </li>
            </ul>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="flex flex-wrap gap-2">
          <Button label="Change Password" class="w-full" raised @click="$emit('changePassword', password, verifyPassword)"></Button>
          <RouterLink v-if="!authStore.forcePasswordChange" to="/myaccount" class="w-full">
            <Button label="Cancel" class="w-full" text raised></Button>
          </RouterLink>
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from "@/stores/auth";
import { usePropsStore } from "@/stores/props";
import { type PasswordRequirements, PasswordRequirementsSchema } from "@/types/Password";

defineEmits<{
  changePassword: [password: string, verifyPassword: string];
}>();

interface Props {
  message: string;
}
const props = defineProps<Props>();

const password = ref("");
const verifyPassword = ref("");
const authStore = useAuthStore();
const propsStore = usePropsStore();

const requirements = ref<PasswordRequirements>();

onMounted(async (): Promise<void> => {
  if (propsStore.api == null || propsStore.api == "") {
    setTimeout(() => {
      axios
        .get("/account/passwordreqs")
        .then((response) => {
          if (!parseData(PasswordRequirementsSchema, response.data.result, requirements) || requirements.value === undefined) return;
        })
        .catch((e) => errorApi(e));
    }, 250);
  }
});

const hasSpecialCharacter = (): boolean => {
  return /[!@#$%^&*()_+\-=\[\]{};':"\|,\.<>\/?]+/.test(password.value);
};

const getMaxRepeats = (): number => {
  let max = 0;
  let current = 0;
  let previous;

  for (const char of password.value) {
    if (previous && char !== previous) {
      if (current > max) {
        max = current;
      }
      current = 0;
    }
    current++;
    previous = char;
  }

  if (current > max) {
    max = current;
  }

  return max;
};
</script>
